// React dependencies
import React, { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
// DWT components
import LargeCheckbox from './LargeCheckbox';
import Checkbox from './Checkbox';

import { validateEmail } from '../../blocks/utils/general-utils';
import { trackNewsletterSubmit } from '../../analytics';

import '../../css/components/newsletter-signups.css';

/**
 * Newsletter Signup
 *
 * A component to handle the newsletter signup page
 *
 * Child Components:
 * - Checkbox.js
 * - LargeCheckbox.js
 */

const NewsletterSignup = () => {
  const initialRender = useRef( true );

  const [ email, setEmail ] = useState( '' );
  useEffect( () => {
    if ( initialRender.current ) {
      initialRender.current = false;
    } else {
      if ( !validateEmail( email ) ) {
        setEmailError( 'Please enter a valid email.' );
        setInputRed( true );

        setIsEmailValid( false );
        buttonControl();
      } else {
        setEmailError( '' );
        setInputRed( false );

        setIsEmailValid( true );
        buttonControl();
      }
    }
  }, [ email ] );

  const handleOnEmailChange = ( e ) => {
    const enteredValue = e.target.value;
    setEmail( enteredValue );
  };

  const [ isEmailValid, setIsEmailValid ] = useState( false );
  useEffect( () => {
    buttonControl();
  }, [ isEmailValid ] );

  const [ serverError, setServerError ] = useState( '' );
  const [ emailError, setEmailError ] = useState( '' );
  const [ inputRed, setInputRed ] = useState( false );
  const [ showConfirmation, setShowConfirmation ] = useState( false );
  const [ preferenceOnlySection, setPreferenceOnlySection ] = useState( [] );
  const [ submitDisabled, setSubmitDisabled ] = useState( true );
  const [ minMet, setMinMet ] = useState( false );
  const [ checkedPrograms, setCheckedPrograms ] = useState( [] );

  useEffect( () => {
    buttonControl();
  }, [ minMet ] );

  const newsletterSettings = window.DP.newsletter;
  const isCentered = newsletterSettings.text_align === 'center';
  const headerBackgroundColor = newsletterSettings.background_color;
  const headerTextColor = newsletterSettings.text_color;

  const mainNewsletter = newsletterSettings.main_program;
  const newsletterPrefs = newsletterSettings.preferences_section ? newsletterSettings.preferences_section : [];

  useEffect( () => {
    // check if any section is preference only by seeing if any program in that section doesn't have a site_id or preference key and if so, return which section it is by it's index
    const isPreferenceOnly = newsletterPrefs.map( ( section, index ) => {
      return section.program_preferences.some( ( program ) => !program.site_id && !program.preference_key ) ? index : null;
    }).filter( ( item ) => item !== null );
    setPreferenceOnlySection( isPreferenceOnly );
  }, [] );

  const handleAddingProgram = ( program ) => {
    setCheckedPrograms( [ ...checkedPrograms, program ] );
  };
  const handleRemovingPrograms = ( programs ) => {
    if ( Array.isArray( programs ) ) {
      // if programs is an array, remove all programs in the array from checkedPrograms
      setCheckedPrograms( checkedPrograms.filter( ( item ) => !programs.includes( item ) ) );
    } else {
      setCheckedPrograms( checkedPrograms.filter( ( item ) => item !== programs ) );
    }
  };

  // state for main newsletter checkbox
  const [ mainNewsletterChecked, setMainNewsletterChecked ] = useState( false );
  const handleMainNewsletterChange = () => {
    setMainNewsletterChecked( !mainNewsletterChecked );
  };
  useEffect( () => {
    if ( mainNewsletterChecked ) {
      handleAddingProgram( mainNewsletter );
    } else {
      const removePrograms = [];
      removePrograms.push( mainNewsletter );
      // check if there are any preferenceOnlySections and if so, uncheck all programs in that section
      if ( preferenceOnlySection.length > 0 ) {
        preferenceOnlySection.forEach( ( sectionIndex ) => {
          checkedStates[sectionIndex].forEach( ( checkbox, checkboxIndex ) => {
            if ( checkbox ) {
              removePrograms.push( newsletterPrefs[sectionIndex].program_preferences[checkboxIndex] );
              handleCheckboxChange( sectionIndex, checkboxIndex, newsletterPrefs[sectionIndex].program_preferences[checkboxIndex] );
            }
          });
        });
      }
      handleRemovingPrograms( removePrograms );
    }
  }, [ mainNewsletterChecked ] );

  // state for checkboxes in preference sections
  const [ checkedStates, setCheckedStates ] = useState( newsletterPrefs.map( section => {
    return new Array( section.program_preferences.length ).fill( false );
  }) );

  // handle checkbox change
  const handleCheckboxChange = ( sectionIndex, checkboxIndex, program ) => {
    const newCheckedStates = [ ...checkedStates ];
    newCheckedStates[sectionIndex][checkboxIndex] = !newCheckedStates[sectionIndex][checkboxIndex];
    // handle adding or removing program from checkedPrograms array
    if ( newCheckedStates[sectionIndex][checkboxIndex] ) {
      handleAddingProgram( program );
    } else {
      handleRemovingPrograms( program );
    }
    setCheckedStates( newCheckedStates );
  };

  // check if at least one checkbox is checked in any array
  useEffect( () => {
    const isMinMet = checkedStates.some( section => section.some( checkbox => checkbox ) ) || mainNewsletterChecked;
    setMinMet( isMinMet );
  }, [ checkedStates, mainNewsletterChecked ] );

  // check if submit button should be disabled
  const buttonControl = () => {
    isEmailValid && minMet ? setSubmitDisabled( false ) : setSubmitDisabled( true );
  };

  const removeEmptyValues = ( obj ) => {
    const nonEmptyObject = {};
    for ( const [ key, value ] of Object.entries( obj ) ) {
      if ( typeof value === 'object' && value !== null ) {
        const nonEmptyNestedObject = removeEmptyValues( value );
        if ( Object.keys( nonEmptyNestedObject ).length > 0 ) {
          nonEmptyObject[key] = nonEmptyNestedObject;
        }
      } else if ( value !== '' && value !== undefined ) {
        nonEmptyObject[key] = value;
      }
    }
    return nonEmptyObject;
  };

  const handleSubmit = ( e ) => {
    e.preventDefault();

    const jsonObj = {
      emailAddress: email,
      subscriptions: []
    };

    let subs = [];
    let prefs = [];

    const addSubscription = ( siteId, programId, preferenceId, preferenceKey, preferenceText ) => {

      if ( !siteId || !programId ) return;
  
      // if siteId and programId combination does not exist, add it to Subs
      if ( !subs.some( item => item.siteId === siteId && item.programId === programId ) ) {
        subs.push({ siteId, programId });
      }
  
      // skip if preferenceId already exists and not made distinct by a preferenceKey
      if ( preferenceId && !preferenceKey && prefs.some( item =>
        item.siteId === siteId
        && item.programId === programId
        && item.preferenceId === preferenceId ) ) {
        return;
      }
  
      // skip if siteId, programId, preferenceId, and preferenceKey combination exists already
      if ( preferenceId && preferenceKey && prefs.some( item =>
        item.siteId === siteId
        && item.programId === programId
        && item.preferenceId === preferenceId
        && item.preferenceKey === preferenceKey ) ) {
        return;
      }
  
      if ( preferenceId ) {
        prefs.push({ siteId, programId, preferenceId, preferenceKey, preferenceText });
      }
    };

    // add all checked programs to subscriptions array
    checkedPrograms.forEach( program => {
      // if its a preference add it to the main program.
      if ( !program.site_id && !program.program_id && program.preference_id ) {
        addSubscription( mainNewsletter.site_id, mainNewsletter.program_id, program.preference_id, program.preference_key, program.preference_text || program.name );
      } else {
        addSubscription( program.site_id, program.program_id, program.preference_id, program.preference_key, program.preference_text );
      }
    });

    // acquisition code
    jsonObj.acquisitionCode = newsletterSettings.acquisition_code;

    let subscriptions = subs;
    subscriptions.forEach( ( item, index ) => {
      let preferences = prefs
        .filter( pref => pref.siteId === item.siteId && pref.programId === item.programId )
        .map( pref => ({
          preferenceId: pref.preferenceId,
          ...( pref.preferenceKey != null && pref.preferenceKey != '' && { preferenceKey: pref.preferenceKey }),
          ...( pref.preferenceText != null && pref.preferenceText != '' && { preferenceText: pref.preferenceText })
        }) );

      if ( preferences.length > 0 ) {
        subscriptions[index].preferences = preferences;
      }
    });

    jsonObj.subscriptions = subscriptions;

    makeFetchRequest( jsonObj );
  };

  const makeFetchRequest = async( jsonObj ) => {
    const subJson = jsonObj;
    await fetch( '/wp-json/dwt/v2/newsletter-signups', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify( subJson ),
      credentials: 'same-origin'
    })
      .then( ( response ) => {
        return response.json();
      })
      .then( ( data ) => {
        if ( data.response.code === 200 ) {
          trackNewsletterSubmit( data.body, jsonObj, true );
          setShowConfirmation( true );
        } else {
          setServerError( 'Currently unavailable. Please try again later.' );
        }
      })
      .catch( ( error ) => {
        console.error( error );
      });
  };

  return (
    <div className={`newsletter-signup ${isCentered && 'center-all'}`}>

      <div
        className="newsletter-signup-header"
        style={{ backgroundColor: headerBackgroundColor, color: headerTextColor }}
      >
        <div className="container">
          <h1 className="heading-1">{newsletterSettings.headline_text}</h1>
          <div dangerouslySetInnerHTML={{ __html: newsletterSettings.body_text }} />
        </div>
      </div>

      { !showConfirmation ? (
        <div className="newsletter-signup-body">
          {newsletterSettings.headline && <h2 className="heading-2">{newsletterSettings.headline}</h2>}
          <form onSubmit={handleSubmit} className="signup-email-form__form">

            <div className="main-program">
              <h3 className="heading-3 subheading">{mainNewsletter.name}</h3>
              <div className="optin-copy medium-text">
                <Checkbox
                  obj={mainNewsletter}
                  id="main-newsletter-checkbox"
                  checked={mainNewsletterChecked}
                  onChange={handleMainNewsletterChange}
                />
              </div>
            </div>

            <div className={`signup-email-form__input-container ${newsletterSettings.input_style}`}>
              <label className="signup-email-form__label">
                <span
                  className={`signup-email-form__label-text ${!focus && email === '' ? 'is-hidden' : 'is-visible'} ${!inputRed ? '' : 'red-label'}`}
                >
                  Email Address (required)
                </span>
              </label>
              <input
                className={`signup-email-form__input bg-transparent ${!inputRed ? '' : 'has-error'}`}
                id='email'
                type='email'
                name='emailAddress'
                placeholder="Email Address (required)"
                value={email}
                onChange={handleOnEmailChange}
              />

              <p className={`paragraph-3 error-message email-error text-error-red mx-0 ${emailError === '' ? 'make-transparent' : 'make-opaque'}`}>{emailError}</p>
            </div>

            { newsletterPrefs.map( ( section, sectionIndex ) => {
              const sectionCheckedState = checkedStates[sectionIndex];
              const isBackgroundEnabled = section.enable_background;
              let checkboxAreaClass;
              switch ( section.program_types ) {
              case 'Group':
                checkboxAreaClass = 'checkbox-area-external';
                break;
              case 'Standalone':
                checkboxAreaClass = 'checkbox-area-single';
                break;
              default:
                checkboxAreaClass = 'checkbox-area';
              };
              // if Standalone is more than one program, use default class
              if ( section.program_types === 'Standalone' && section.program_preferences.length > 1 ) {
                checkboxAreaClass = 'checkbox-area';
              }

              return (
                <div className="signup-subsection newsletter-subsection" key={sectionIndex}>
                  { section.title && <h4 className="heading-4 subheading">{section.title}</h4>}
                  { section.body_text && (
                    <div className="optin-copy medium-text">
                      {section.body_text}
                    </div>
                  )}
                  <div className={`parent ${checkboxAreaClass} ${isBackgroundEnabled && 'enable-background'}`}>
                    { section.preferences_subhead && (
                      <p className="newsletter-headline medium-text"><strong>{section.preferences_subhead}</strong></p>
                    )}
                    { ( preferenceOnlySection.includes( sectionIndex ) && !mainNewsletterChecked ) && (
                      <div className="cover" />
                    )}
                    <ul className="newsletter-preferences checkboxes">
                      {section.program_preferences.map( ( element, checkboxIndex ) => {
                        return (
                          <li key={checkboxIndex} className={`small-text ${section.program_types.length > 1 && 'multi-checkbox-item'}`}>
                            { section.program_types === 'Group' && (
                              <LargeCheckbox
                                obj={element}
                                id={`custom-lg-checkbox-${sectionIndex}-${checkboxIndex}`}
                                checked={sectionCheckedState[checkboxIndex]}
                                onChange={() => handleCheckboxChange( sectionIndex, checkboxIndex, element )}
                              />
                            )}
                            { section.program_types === 'Preference Section' && (
                              <Checkbox
                                obj={element}
                                id={`custom-checkbox-${sectionIndex}-${checkboxIndex}`}
                                checked={sectionCheckedState[checkboxIndex]}
                                onChange={() => handleCheckboxChange( sectionIndex, checkboxIndex, element )}
                              />
                            )}
                            { section.program_types === 'Standalone' && (
                              <Checkbox
                                obj={element}
                                id={`custom-single-checkbox-${sectionIndex}-${checkboxIndex}`}
                                checked={sectionCheckedState[checkboxIndex]}
                                onChange={() => handleCheckboxChange( sectionIndex, checkboxIndex, element )}
                              />
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              );
            })}
            <div className="button-container">
              {/* Submit button */}
              <button type="submit" disabled={submitDisabled} className="signup-button btn--primary">Sign Up</button>
            </div>
            {/* Legal copy */}
            {newsletterSettings.legal_copy && <div className="legal-copy" dangerouslySetInnerHTML={{ __html: newsletterSettings.legal_copy }} />}

            <p className={`paragraph-3 server-error text-error-red ${serverError === '' ? 'make-transparent' : 'make-opaque'}`}>{serverError}</p>
          </form>
        </div> )
        : ( <div className="newsletter-signup-body">
          <div className="confirmation">
            <h4 className="heading-4">Thanks for signing up!</h4>
            <p className="confirmation-copy">Refresh the page if you would like to submit a new form.</p>
            <a href="/" className="backhome-button btn--primary">Back to Homepage</a>
          </div>
        </div> )

      }

    </div>
  );
};

const renderNewsletterSignup = () => {
  const newsletterSignup = document.querySelector( '#newsletter-root' );

  if ( !newsletterSignup ) {
    return;
  }

  const root = createRoot( newsletterSignup );
  root.render( <NewsletterSignup /> );
};

export default renderNewsletterSignup;
